/* Invictus additions: cart coupon box, product offer strip, light/dark toggle. */
(() => {
  const root = () => (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

  // Message shown after the cart re-renders (the coupon box is rebuilt with it).
  const FLASH = 'invCouponFlash';
  let memFlash = null;
  const flash = {
    set(value) {
      memFlash = value;
      try { sessionStorage.setItem(FLASH, JSON.stringify(value)); } catch (e) {}
    },
    take(key) {
      let value = memFlash;
      try { value = JSON.parse(sessionStorage.getItem(FLASH)) || value; } catch (e) {}
      if (!value || value.key !== key) return null;
      memFlash = null;
      try { sessionStorage.removeItem(FLASH); } catch (e) {}
      return value;
    }
  };

  const setDiscounts = async (codes) => {
    const res = await fetch(root() + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: codes.join(',') })
    });
    if (!res.ok) throw new Error('cart/update ' + res.status);
    return res.json();
  };

  const sameCode = (a, b) => a.toLowerCase() === b.toLowerCase();
  const workingCodes = (cart) => (cart.discount_codes || []).filter((d) => d.applicable).map((d) => d.code);
  const isApplicable = (cart, code) => workingCodes(cart).some((c) => sameCode(c, code));

  // Adds a code next to the ones already working; a code that doesn't apply is dropped
  // again so it never replaces a valid one.
  const applyDiscount = async (code) => {
    const current = await fetch(root() + 'cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
    const kept = workingCodes(current).filter((c) => !sameCode(c, code));
    const cart = await setDiscounts([...kept, code]);
    if (isApplicable(cart, code) || !kept.length) return cart;
    return setDiscounts(kept);
  };

  const removeDiscount = async (code) => {
    const current = await fetch(root() + 'cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
    return setDiscounts(workingCodes(current).filter((c) => !sameCode(c, code)));
  };

  class InvictusCoupons extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.msg = this.querySelector('.inv-coupons__msg');
      this.input = this.querySelector('.inv-coupons__input');
      // Enter must not submit the surrounding cart form
      this.input.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        this.apply(this.input.value);
      });
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-inv-submit]')) this.apply(this.input.value);
        const offer = event.target.closest('[data-inv-apply]');
        if (offer) this.apply(offer.dataset.invApply);
        const remove = event.target.closest('[data-inv-remove]');
        if (remove) this.apply(remove.dataset.invRemove, true);
      });
      const note = flash.take(this.dataset.key);
      if (note) this.show(note.text, note.type);
    }

    show(text, type) {
      this.msg.textContent = text;
      this.msg.dataset.type = type || '';
    }

    async apply(code, removing) {
      code = (code || '').trim();
      if (!code) return this.show(this.dataset.msgEmpty, 'error');
      this.classList.add('is-busy');
      try {
        const cart = removing ? await removeDiscount(code) : await applyDiscount(code);
        let note;
        if (removing) note = { type: '', text: this.dataset.msgRemoved };
        else if (isApplicable(cart, code)) note = { type: 'ok', text: this.dataset.msgOk.replace('[code]', code.toUpperCase()) };
        else note = { type: 'error', text: this.dataset.msgBad.replace('[code]', code.toUpperCase()) };
        flash.set({ ...note, key: this.dataset.key });
        this.refresh(cart);
      } catch (e) {
        this.show(this.dataset.msgFail, 'error');
      } finally {
        this.classList.remove('is-busy');
      }
    }

    refresh(cart) {
      const update = window.sharedFunctions && window.sharedFunctions.updateSidebarCart;
      if (this.closest('#halo-cart-sidebar') && update) update(cart);
      else window.location.reload();
    }
  }

  class InvictusOffer extends HTMLElement {
    connectedCallback() {
      const button = this.querySelector('[data-inv-offer-apply]');
      if (!button || this.ready) return;
      this.ready = true;
      button.addEventListener('click', async () => {
        this.classList.add('is-busy');
        try {
          const cart = await applyDiscount(this.dataset.code);
          // a code that doesn't match the cart yet stays saved and applies once eligible items are added
          button.disabled = true;
          button.textContent = this.dataset.labelDone;
          this.querySelector('.inv-offer__msg').textContent = this.dataset.msgOk;
          this.classList.add('is-applied');
        } catch (e) {
          window.location.href = root() + 'discount/' + encodeURIComponent(this.dataset.code) + '?redirect=' + encodeURIComponent(location.pathname);
        } finally {
          this.classList.remove('is-busy');
        }
      });
    }
  }

  // ---------- quick add from product cards ----------
  // [data-inv-quick-add="handle"]: adds straight to the cart when data-inv-variant is set,
  // otherwise opens a small sheet to pick the options first.
  const isAr = () => document.documentElement.lang === 'ar';
  const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const t = (en, ar) => (isAr() ? ar : en);

  const formatMoney = (cents, format) => {
    const amount = (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const noDecimals = Math.round(cents / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return (format || '{{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (m, key) => (key.includes('no_decimals') ? noDecimals : amount));
  };

  const openCartDrawer = async () => {
    const cart = await fetch(root() + 'cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
    const update = window.sharedFunctions && window.sharedFunctions.updateSidebarCart;
    if (update && document.getElementById('halo-cart-sidebar')) {
      document.body.classList.add('cart-sidebar-show');
      update(cart);
    } else {
      window.location.href = root() + 'cart';
    }
  };

  const addVariant = async (id) => {
    const res = await fetch(root() + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] })
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.description || data.message || 'add failed');
    }
  };

  let sheet;
  const buildSheet = () => {
    sheet = document.createElement('div');
    sheet.className = 'inv-qa';
    sheet.hidden = true;
    sheet.innerHTML = `
      <div class="inv-qa__backdrop" data-inv-qa-close></div>
      <div class="inv-qa__panel" role="dialog" aria-modal="true" aria-labelledby="InvQaTitle">
        <button type="button" class="inv-qa__close" data-inv-qa-close aria-label="${t('Close', 'إغلاق')}">×</button>
        <div class="inv-qa__head">
          <img class="inv-qa__img" alt="" width="80" height="80">
          <div><p class="inv-qa__title" id="InvQaTitle"></p><p class="inv-qa__price"></p></div>
        </div>
        <div class="inv-qa__options"></div>
        <p class="inv-qa__msg" role="status" aria-live="polite"></p>
        <button type="button" class="inv-qa__add"></button>
      </div>`;
    document.body.appendChild(sheet);
    sheet.addEventListener('click', (event) => {
      if (event.target.closest('[data-inv-qa-close]')) closeSheet();
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !sheet.hidden) closeSheet();
    });
  };

  const closeSheet = () => {
    if (!sheet) return;
    sheet.hidden = true;
    document.documentElement.classList.remove('inv-qa-open');
    if (sheet.opener) sheet.opener.focus();
  };

  const openSheet = async (opener) => {
    if (!sheet) buildSheet();
    const money = opener.dataset.invMoney;
    const product = await fetch(root() + 'products/' + opener.dataset.invQuickAdd + '.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
    const options = (product.options || []).map((o) => (typeof o === 'string' ? { name: o, values: [] } : o));
    let current = product.variants.find((v) => v.available) || product.variants[0];
    const chosen = [current.option1, current.option2, current.option3].slice(0, options.length);

    const $ = (sel) => sheet.querySelector(sel);
    $('.inv-qa__title').textContent = product.title;
    const img = product.featured_image ? (product.featured_image.startsWith('//') ? 'https:' + product.featured_image : product.featured_image) : '';
    $('.inv-qa__img').src = img ? img + (img.includes('?') ? '&' : '?') + 'width=160' : '';
    $('.inv-qa__msg').textContent = '';

    const findVariant = () => product.variants.find((v) => chosen.every((val, i) => v['option' + (i + 1)] === val));
    const render = () => {
      current = findVariant();
      const ok = current && current.available;
      $('.inv-qa__price').textContent = current ? formatMoney(current.price, money) : '';
      const add = $('.inv-qa__add');
      add.disabled = !ok;
      add.textContent = ok ? t('Add to cart', 'أضف للسلة') : t('Sold out', 'نفدت الكمية');
      $('.inv-qa__options').innerHTML = options.map((opt, i) => {
        const values = opt.values.length ? opt.values : [...new Set(product.variants.map((v) => v['option' + (i + 1)]))];
        const chips = values.map((val) => {
          const test = chosen.slice();
          test[i] = val;
          const match = product.variants.find((v) => test.every((x, j) => v['option' + (j + 1)] === x));
          const soldOut = !match || !match.available;
          const on = chosen[i] === val;
          return `<button type="button" class="inv-qa__chip${on ? ' is-on' : ''}${soldOut ? ' is-out' : ''}" data-i="${i}" data-val="${esc(val)}" aria-pressed="${on}">${esc(val)}</button>`;
        }).join('');
        return `<fieldset class="inv-qa__group"><legend>${esc(opt.name)}</legend><div class="inv-qa__chips">${chips}</div></fieldset>`;
      }).join('');
    };
    $('.inv-qa__options').onclick = (event) => {
      const chip = event.target.closest('.inv-qa__chip');
      if (!chip) return;
      chosen[Number(chip.dataset.i)] = chip.dataset.val;
      render();
    };
    $('.inv-qa__add').onclick = async () => {
      if (!current || !current.available) return;
      sheet.classList.add('is-busy');
      try {
        await addVariant(current.id);
        closeSheet();
        await openCartDrawer();
      } catch (e) {
        $('.inv-qa__msg').textContent = e.message && e.message !== 'add failed' ? e.message : t('Something went wrong, please try again', 'حدث خطأ، حاول مرة أخرى');
      } finally {
        sheet.classList.remove('is-busy');
      }
    };
    render();
    sheet.opener = opener;
    sheet.hidden = false;
    document.documentElement.classList.add('inv-qa-open');
    $('.inv-qa__add').focus();
  };

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-inv-quick-add]');
    if (!button) return;
    event.preventDefault();
    if (button.classList.contains('is-busy')) return;
    button.classList.add('is-busy');
    try {
      if (button.dataset.invVariant) {
        await addVariant(button.dataset.invVariant);
        await openCartDrawer();
      } else {
        await openSheet(button);
      }
    } catch (e) {
      window.location.href = root() + 'products/' + button.dataset.invQuickAdd;
    } finally {
      button.classList.remove('is-busy');
    }
  });

  if (!customElements.get('invictus-coupons')) customElements.define('invictus-coupons', InvictusCoupons);
  if (!customElements.get('invictus-offer')) customElements.define('invictus-offer', InvictusOffer);

  // Light / dark toggle. The saved choice is applied early by an inline script in <head>.
  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-inv-theme-toggle]');
    if (!toggle) return;
    const html = document.documentElement;
    const next = html.dataset.invTheme === 'light' ? 'dark' : 'light';
    html.dataset.invTheme = next;
    try { localStorage.setItem('invTheme', next); } catch (e) {}
    document.querySelectorAll('[data-inv-theme-toggle]').forEach((b) => b.setAttribute('aria-pressed', String(next === 'light')));
  });
})();
